import { useState, useEffect } from "react";
import {
  RewardType,
  type VoucherkuCategory,
} from "../../../../domain/model/RewardCatalogModel";
import {
  FetchVoucherkuCategoriesUseCase,
  FetchVoucherkuProductsUseCase,
} from "../../../../domain/usecase";
import { Input } from "@/components/ui/input";
import { Select, type SelectOption } from "@/components/ui/select/select";

export interface RewardTypeSelectorSectionProps {
  readonly type: string;
  readonly title: string;
  readonly titleError?: string;
  readonly isEditMode?: boolean;
  readonly voucherkuItem?: string;
  readonly voucherkuItemError?: string;
  readonly voucherkuCategoryId?: string;
  readonly onChangeType: (type: string) => void;
  readonly onChangeTitle: (value: string) => void;
  readonly onChangeVoucherkuItem?: (value: string) => void;
  readonly onAutoFillVoucherku?: (data: {
    title: string;
    description: string;
    quantity: string;
    productCode: string;
    categoryId: string;
  }) => void;
}

export const RewardTypeSelectorSection = ({
  type,
  title,
  titleError,
  isEditMode = false,
  voucherkuItemError,
  voucherkuCategoryId = "",
  onChangeType,
  onChangeTitle,
  onChangeVoucherkuItem,
  onAutoFillVoucherku,
}: RewardTypeSelectorSectionProps): React.JSX.Element => {
  // Category state (shown as "Voucherku Item" dropdown)
  const [categories, setCategories] = useState<readonly VoucherkuCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  // Fetch categories on mount when voucherku type is selected
  useEffect(() => {
    if (type !== RewardType.VOUCHERKU) return;

    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const data = await FetchVoucherkuCategoriesUseCase();
        setCategories(data);
      } catch (error) {
        console.error("Failed to fetch voucherku categories:", error);
        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    void fetchCategories();
  }, [type]);

  const handleCategorySelect = async (categoryId: string) => {
    const category = categories.find((c) => c.categoryId === categoryId);
    if (!category) return;

    // Fetch products for this category and auto-fill with first product
    setIsAutoFilling(true);
    try {
      const products = await FetchVoucherkuProductsUseCase(category.categoryId);
      if (products.length > 0) {
        const firstProduct = products[0];
        // Store product code in voucherkuItem field (used as productCode in payload)
        onChangeVoucherkuItem?.(firstProduct.productCode);
        onAutoFillVoucherku?.({
          title: firstProduct.productName,
          description: firstProduct.description || "",
          quantity: String(firstProduct.stock ?? 0),
          productCode: firstProduct.productCode,
          categoryId: firstProduct.categoryId,  
        });
      } else {
        // No products found, store category ID as fallback
        onChangeVoucherkuItem?.(category.categoryId);
      }
    } catch (error) {
      console.error("Failed to fetch voucherku products:", error);
      // On error, store category ID as fallback
      onChangeVoucherkuItem?.(category.categoryId);
    } finally {
      setIsAutoFilling(false);
    }
  };

  const isVoucherkuType = type === RewardType.VOUCHERKU;

  // Build options for Select component
  const categoryOptions: SelectOption[] = isLoadingCategories
    ? [{ value: "", label: "Loading...", disabled: true }]
    : isAutoFilling
      ? [{ value: "", label: "Loading product...", disabled: true }]
      : categories.length === 0
        ? [{ value: "", label: "No items found", disabled: true }]
        : [
            { value: "", label: "Select Voucherku Item", disabled: true },
            ...categories.map((category) => ({
              value: category.categoryId,
              label: category.name,
            })),
          ];

  return (
    <div className="grid grid-cols-2 gap-4">
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-gray-800 m-0 text-left">
          Type
        </h3>
        <div className="flex justify-between flex-wrap gap-4 mr-4">
          <label
            className={`${
              isEditMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } flex items-center gap-2 text-sm text-gray-800 font-medium`}
          >
            <div className="relative flex items-center justify-center">
              <input
                type="radio"
                name="rewardType"
                value={RewardType.VOUCHER}
                checked={type === RewardType.VOUCHER}
                onChange={(event) => onChangeType(event.target.value)}
                className={`${
                  isEditMode ? "cursor-not-allowed" : "cursor-pointer"
                } appearance-none w-4 h-4 border-2 border-gray-300 rounded-full checked:border-[#001a41] checked:border-[5px] transition-all`}
                disabled={isEditMode}
              />
            </div>
            <span>Voucher</span>
          </label>
          <label
            className={`${
              isEditMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } flex items-center gap-2 text-sm text-gray-800 font-medium`}
          >
            <div className="relative flex items-center justify-center">
              <input
                type="radio"
                name="rewardType"
                value={RewardType.E_WALLET_BALANCE}
                checked={type === RewardType.E_WALLET_BALANCE}
                onChange={(event) => onChangeType(event.target.value)}
                className={`${
                  isEditMode ? "cursor-not-allowed" : "cursor-pointer"
                } appearance-none w-4 h-4 border-2 border-gray-300 rounded-full checked:border-[#001a41] checked:border-[5px] transition-all`}
                disabled={isEditMode}
              />
            </div>
            <span>E-Wallet Balance</span>
          </label>
          <label
            className={`${
              isEditMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } flex items-center gap-2 text-sm text-gray-800 font-medium`}
          >
            <div className="relative flex items-center justify-center">
              <input
                type="radio"
                name="rewardType"
                value={RewardType.POINT_CONVERSION}
                checked={type === RewardType.POINT_CONVERSION}
                onChange={(event) => onChangeType(event.target.value)}
                className={`${
                  isEditMode ? "cursor-not-allowed" : "cursor-pointer"
                } appearance-none w-4 h-4 border-2 border-gray-300 rounded-full checked:border-[#001a41] checked:border-[5px] transition-all`}
                disabled={isEditMode}
              />
            </div>
            <span>Point Conversion</span>
          </label>
          <label
            className={`${
              isEditMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } flex items-center gap-2 text-sm text-gray-800 font-medium`}
          >
            <div className="relative flex items-center justify-center">
              <input
                type="radio"
                name="rewardType"
                value={RewardType.VOUCHERKU}
                checked={type === RewardType.VOUCHERKU}
                onChange={(event) => onChangeType(event.target.value)}
                className={`${
                  isEditMode ? "cursor-not-allowed" : "cursor-pointer"
                } appearance-none w-4 h-4 border-2 border-gray-300 rounded-full checked:border-[#001a41] checked:border-[5px] transition-all`}
                disabled={isEditMode}
              />
            </div>
            <span>Voucherku</span>
          </label>
        </div>
      </section>

      {/* Show Voucherku Item dropdown (categories) when voucherku type is selected, otherwise show Title */}
      {isVoucherkuType ? (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="voucherkuItem"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Voucherku Item
          </label>
          <Select
            id="voucherkuItem"
            value={voucherkuCategoryId}
            onChange={(e) => void handleCategorySelect(e.target.value)}
            options={categoryOptions}
            disabled={isLoadingCategories || isAutoFilling || isEditMode}
            errorMessage={voucherkuItemError}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="title"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Title
          </label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(event) => onChangeTitle(event.target.value)}
            placeholder="Title"
            errorMessage={titleError}
          />
        </div>
      )}
    </div>
  );
};
